var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { v4 as uuidv4 } from 'uuid';
import getURLParam from '~/utils/getURLParam';
import { keysToCamel } from '~/utils/toCamelCase';
import { keysToSnake } from '~/utils/toSnakeCase';
export const Websocket = class {
    constructor(userId, organization, navigate, setErrorModalMessage) {
        this.callbacks = {};
        this.eventsFailedByAuthorization = [];
        this.isReauthorizing = false;
        this.parts = {};
        this.pendingQueue = [];
        this.retryConnection = true;
        this.connect = () => {
            const ws = new WebSocket(window.RG_CONFIG.WEB_SOCKET_URL);
            ws.onopen = this.onOpen;
            ws.onclose = this.onClose;
            ws.onmessage = this.onMessage;
            ws.onerror = this.onError;
            this.ws = ws;
        };
        this.onOpen = () => {
            window.debugLogger('WebSocket connection established.');
            const queued = this.pendingQueue;
            this.pendingQueue = [];
            queued.forEach(fn => fn());
        };
        this.onClose = () => {
            window.debugLogger('Websocket disconnected.');
        };
        this.onMessage = (event) => {
            try {
                const data = keysToCamel(JSON.parse(event.data));
                if (!this.callbacks[data.eventId])
                    return;
                try {
                    if (data.statusCode === 206)
                        return this.partCallback(data);
                    if (data.statusCode === 403)
                        return this.unauthorizedEventCallback();
                    this.callbacks[data.eventId].callback(data, this.navigate);
                    delete this.callbacks[data.eventId];
                }
                catch (err) {
                    console.error(err);
                    this.callbacks[data.eventId].callback(Object.assign(Object.assign({}, data), { statusCode: 500, payload: err }));
                }
            }
            catch (err) {
                this.openErrorModal('WebSocket Error in onMessage: ' + JSON.stringify(err));
            }
        };
        this.onError = () => {
            if (this.ws.readyState !== 1 && this.retryConnection) {
                window.debugLogger('WebSocket connection failed. Retrying...');
                this.retryConnection = false;
                this.connect();
                return;
            }
            if (this.ws.readyState !== 1) {
                this.openErrorModal('Failed to connect to WebSocket Service');
                return;
            }
            this.openErrorModal('Unhandled Exception');
        };
        this.send = (eventType, payload, callback, eventId) => __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.ws.readyState !== 1) {
                    window.debugLogger('WebSocket not open; queueing event until connection is established.');
                    this.pendingQueue.push(() => this.send(eventType, payload, callback, eventId));
                    return;
                }
                payload = Object.assign(Object.assign({}, payload), { organizationId: this.organization.id });
                eventId = eventId || uuidv4();
                yield this.ws.send(JSON.stringify(keysToSnake({ eventType, eventId, payload }, ['metadata', 'advancedFilters'])));
                this.callbacks[eventId] = { eventId, payload, callback };
            }
            catch (err) {
                this.openErrorModal('WebSocket Error in send: ' + JSON.stringify(err));
            }
        });
        this.partCallback = (data) => {
            try {
                if (!this.parts[data.eventId]) {
                    this.parts[data.eventId] = Array(data.partCount).fill(null);
                }
                if (data.partIndex === undefined)
                    return;
                this.parts[data.eventId][data.partIndex] = data.payload;
                if (this.parts[data.eventId].every(part => part !== null)) {
                    const completeParts = this.parts[data.eventId].join('');
                    const payload = keysToCamel(JSON.parse(completeParts));
                    this.callbacks[data.eventId].callback(Object.assign(Object.assign({}, data), { statusCode: 200, payload }), this.navigate);
                    delete this.callbacks[data.eventId];
                }
            }
            catch (err) {
                this.openErrorModal('WebSocket Error in partsCallback: ' + JSON.stringify(err));
            }
        };
        this.unauthorizedEventCallback = () => {
            console.warn('Received Unauthorized response from WebSocket.');
            window.routeToErrorPage(new Error('Received Unauthorized response from WebSocket.'));
        };
        this.retryFailedEvents = () => {
            for (const socketRequest of this.eventsFailedByAuthorization) {
                this.send(socketRequest.eventType, socketRequest.payload, socketRequest.callback, socketRequest.eventId);
            }
            this.eventsFailedByAuthorization = [];
        };
        this.close = () => {
            this.ws.close();
        };
        this.userId = userId;
        this.organization = organization;
        this.navigate = navigate;
        this.openErrorModal = (message) => setErrorModalMessage(message);
        this.isDevMode = getURLParam('DEV_MODE') !== null;
        this.connect();
    }
};
